import { db } from "@/repositories/database";
import type { DRDrawing } from "@/types";
import type { RepositoryBaseInterface } from "@/repositories/repository.interface";
import { BaseRepository } from "@/repositories/base.repository";

class DrawingRepository
  extends BaseRepository<DRDrawing, number>
  implements RepositoryBaseInterface<DRDrawing>
{
  private currentOrganizationId: number | null = null;

  constructor() {
    super(db.files, "file");
  }

  setCurrentOrganizationId(organizationId: number) {
    this.currentOrganizationId = organizationId;
  }

  fetchAllByName() {
    const files = db.files.orderBy("name");
    return files;
  }

  fetchAllByNewest() {
    if (!this.currentOrganizationId) {
      return [];
    }
    const drawings = db.drawings
      .where("organizationId")
      .equals(this.currentOrganizationId)
      .reverse()
      .sortBy("createdAt");
    return drawings;
  }

  async update(fileId: number, update: Partial<Omit<DRDrawing, "id">>) {
    return await db.files.update(fileId, update);
  }

  replace(drawing: DRDrawing) {
    return db.files.put(drawing);
  }

  createMany(items: DRDrawing[]) {
    return db.transaction(
      "rw",
      db.files,
      async (): Promise<{ created: number[] }> => {
        const { failed, created } = await this.createMany2(items);
        if (failed.length) {
          // already existed, so we're updating them
          await Promise.all(
            failed.map(async (failedId) => {
              const failedItem = items.find((item) => item.id === failedId);
              if (!failedItem) {
                throw new Error(
                  "Item which insertion was rejected cannot be found in initial items array"
                );
              }

              const existing = await db.files.get(failedId);
              if (!existing) {
                throw new Error(
                  "Item which insertion was rejected cannot be found in db"
                );
              }
            })
          );
        }
        return { created };
      }
    );
  }

  createManyFromServer(items: DRDrawing[]) {
    return this.upsertMany(items);
  }

  async getMany(filters: { projectId?: number }) {
    if (!this.currentOrganizationId) return [];
    const organizationFiles = await db.files
      .where("organizationId")
      .equals(this.currentOrganizationId);
    if (filters.projectId)
      return await organizationFiles
        .and((file) => file.projectId === filters.projectId)
        .toArray();

    return await organizationFiles.toArray();
  }

  /** Plans rattachés à un ouvrage, depuis le cache local (repli hors ligne). */
  async getManyByDiagnostic(diagnosticId: number): Promise<DRDrawing[]> {
    if (!this.currentOrganizationId) return [];
    return await db.files
      .where("organizationId")
      .equals(this.currentOrganizationId)
      .and((file) => file.diagnostic?.id === diagnosticId)
      .toArray();
  }

  deleteById(id: number) {
    return db.files.delete(id);
  }

  deleteManyById(ids: number[]) {
    return db.files.bulkDelete(ids);
  }

  getAllIds() {
    if (!this.currentOrganizationId) return [];
    return db.files
      .where("organizationId")
      .equals(this.currentOrganizationId)
      .toArray()
      .then((files) => files.map((file) => file.id));
  }

  getManyByIds(ids: number[]): Promise<DRDrawing[]> {
    if (!this.currentOrganizationId) return Promise.resolve([]);
    return db.files
      .bulkGet(ids)
      .then((results) =>
        results.filter(
          (item): item is DRDrawing =>
            item !== undefined &&
            item.organizationId === this.currentOrganizationId
        )
      );
  }
}

export const drawingRepository = new DrawingRepository();
